// Filtro explícito por seller das listas do admin (transações, saques), ADMIN-SELLER-ID.
// Sem seller escolhido: campo de busca (nome, e-mail, documento ou id) que consulta
// `GET /admin/v1/sellers?q=` com debounce e lista as identidades; escolher uma aplica o
// `seller_id`. Com seller escolhido: chip removível com a identidade dele.
//
// Consulta a LISTA de sellers, não o detalhe: abrir o detalhe grava `seller.view` na
// auditoria e filtrar não é abrir.

import { useQuery } from '@tanstack/react-query';
import { useEffect, useId, useState } from 'react';
import { adminSellerLookupOptions } from '../../api/queries/admin-sellers.ts';
import { Btn } from '../../components/kit/btn.tsx';
import { Mono } from '../../components/kit/mono.tsx';
import { SearchInput } from '../../components/kit/search-input.tsx';
import { type SellerIdentityData, sellerTitle } from './seller-identity.tsx';

const DEBOUNCE_MS = 300;
const MIN_CHARS = 2;

function useDebounced(value: string): string {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebounced(value);
    }, DEBOUNCE_MS);
    return () => {
      clearTimeout(timer);
    };
  }, [value]);
  return debounced;
}

/** Chip do seller filtrado: título e e-mail quando a lookup já respondeu, o id enquanto isso. */
function SellerChip({ sellerId, onClear }: { sellerId: string; onClear: () => void }) {
  const lookup = useQuery(adminSellerLookupOptions(sellerId, true));
  const seller: SellerIdentityData | undefined = lookup.data?.data.find(
    (row) => row.id === sellerId,
  );
  const title = seller === undefined ? sellerId : sellerTitle(seller);
  return (
    <Btn small icon="close" aria-label={`Remover o filtro de seller: ${title}`} onClick={onClear}>
      <span className="flex min-w-0 items-center gap-2">
        {seller === undefined ? (
          <Mono ink="text">{sellerId}</Mono>
        ) : (
          <>
            <span className="truncate font-medium">{title}</span>
            {seller.email != null && (
              <span className="hidden truncate text-muted sm:inline">{seller.email}</span>
            )}
          </>
        )}
      </span>
    </Btn>
  );
}

function SellerSearch({ onPick }: { onPick: (sellerId: string) => void }) {
  const [text, setText] = useState('');
  const term = useDebounced(text.trim());
  const listId = useId();
  const enabled = term.length >= MIN_CHARS;
  const lookup = useQuery(adminSellerLookupOptions(term, enabled));
  const results = enabled ? (lookup.data?.data ?? []) : [];
  const typed = text.trim().length >= MIN_CHARS;

  return (
    <div className="relative w-full sm:w-[280px]">
      <SearchInput
        small
        label="Filtrar por seller"
        placeholder="Seller: nome, e-mail, documento ou id"
        value={text}
        aria-controls={listId}
        onChange={(event) => {
          setText(event.target.value);
        }}
      />
      {typed && (
        <ul
          id={listId}
          aria-label="Sellers encontrados"
          className="absolute z-20 mt-1 max-h-72 w-full min-w-[280px] overflow-auto rounded-xl border border-border bg-card p-1 shadow-(--shadow-card)"
        >
          {term !== text.trim() || lookup.isFetching ? (
            <li className="px-3 py-2 text-12 text-muted">Buscando…</li>
          ) : results.length === 0 ? (
            <li className="px-3 py-2 text-12 text-muted">Nenhum seller encontrado</li>
          ) : (
            results.map((seller) => (
              <li key={seller.id}>
                <button
                  type="button"
                  aria-label={`Filtrar por ${sellerTitle(seller)}`}
                  onClick={() => {
                    onPick(seller.id);
                    setText('');
                  }}
                  className="flex w-full min-w-0 flex-col rounded-lg px-3 py-2 text-left leading-tight hover:bg-secondary focus-visible:bg-secondary"
                >
                  <span className="truncate text-13 font-medium">{sellerTitle(seller)}</span>
                  <span className="truncate text-11 text-muted">
                    {seller.email ?? 'sem e-mail'}
                  </span>
                  <Mono size={10}>{seller.id}</Mono>
                </button>
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
}

export function SellerFilter({
  value,
  onChange,
}: {
  /** Id público (`sel_...`) do seller filtrado, ou `undefined`. */
  value: string | undefined;
  onChange: (sellerId: string | undefined) => void;
}) {
  if (value !== undefined) {
    return (
      <SellerChip
        sellerId={value}
        onClear={() => {
          onChange(undefined);
        }}
      />
    );
  }
  return <SellerSearch onPick={onChange} />;
}
